import { useState } from "react";
import { api } from "@/lib/api";
import { Copy, Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PITCHES = [
  {
    key: "full",
    name: "Official sales pitch — Full",
    use: "Emails, LinkedIn posts, pitch decks, long WhatsApp broadcasts",
    text: `Greetings, great and smart world-class entrepreneurs and vendors!

We present VISMARKS — a world-class platform built to sell your products globally at light-speed. Whether you import electronics, retail fashion, or craft by hand, VISMARKS was built for you.

Unlike major marketplaces that take a heavy cut of every sale, here you keep up to 99.8% of your sales proceeds. You pay just one flat monthly fee — no commissions eating your profit — and upload unlimited products to your store.

Our AI lets you list a product by voice in 60 seconds, creates studio-quality photos, and translates your shop into 36 languages so buyers from Lagos to Tokyo can find you. Every order is covered by secure buyer protection until delivery — so customers buy with confidence, and you sell with credibility.

🎁 Founding sellers enjoy their first month completely FREE.

It's the smartest way to scale your business. Join our global network of sellers today → vismarks.com`,
  },
  {
    key: "short",
    name: "Official sales pitch — Short",
    use: "WhatsApp status, SMS, X, group posts, flier captions",
    text: `⚡ Sell globally at light-speed on VISMARKS. Keep up to 99.8% of every sale — one flat fee, zero commissions, unlimited products. AI lists your product by voice in 60 seconds in 36 languages, with buyer protection on every order. Founding sellers: first month FREE → vismarks.com`,
  },
];

const LANGS = ["French", "Swahili", "Hausa"];

export const SalesPitchPanel = () => {
  const [translated, setTranslated] = useState({});
  const [busy, setBusy] = useState("");

  const translate = async (p, lang) => {
    setBusy(p.key + lang);
    try {
      const { data } = await api.post("/campaigns/translate-caption", { caption: p.text, language: lang }, { timeout: 60000 });
      setTranslated((t) => ({ ...t, [p.key]: { lang, text: data.text } }));
    } catch { toast.error("Translation failed — try again"); }
    finally { setBusy(""); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Pitch copied — paste anywhere 🚀"); };

  return (
    <div className="mt-5 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="sales-pitch-panel">
      <div className="text-xs uppercase tracking-widest text-emerald-400 font-bold">📣 Official VISMARKS sales pitch</div>
      <div className="mt-3 grid md:grid-cols-2 gap-3">
        {PITCHES.map((p) => (
          <div key={p.key} className="rounded-lg border border-[#27272A] bg-black/30 p-3" data-testid={`pitch-${p.key}`}>
            <div className="text-[11px] font-bold text-white">{p.name}</div>
            <div className="text-[10px] text-neutral-500">{p.use}</div>
            <div className="mt-2 text-xs text-neutral-300 whitespace-pre-wrap max-h-36 overflow-y-auto pr-1">
              {translated[p.key]?.text || p.text}
            </div>
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <button onClick={() => copy(translated[p.key]?.text || p.text)} data-testid={`pitch-copy-${p.key}`}
                className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">
                <Copy className="h-3 w-3" /> Copy {translated[p.key] ? translated[p.key].lang : "English"}
              </button>
              {translated[p.key] && (
                <button onClick={() => setTranslated((t) => ({ ...t, [p.key]: null }))}
                  className="text-[10px] text-neutral-500 hover:text-white underline">English</button>
              )}
              {LANGS.map((l) => (
                <button key={l} onClick={() => translate(p, l)} disabled={busy === p.key + l} data-testid={`pitch-translate-${p.key}-${l.toLowerCase()}`}
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border border-[#27272A] text-neutral-400 hover:text-white disabled:opacity-50">
                  {busy === p.key + l ? <Loader2 className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />} {l}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="pitch-video-row">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">🎬 30s pitch video:</span>
        <a href="/campaign/pitch-video-script.txt" download data-testid="pitch-script-link"
          className="text-[10px] font-bold px-2.5 py-1 rounded-full border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">
          ⬇ Shot-by-shot script
        </a>
        <a href="/campaign/pitch-voiceover.mp3" download data-testid="pitch-voiceover-link"
          className="text-[10px] font-bold px-2.5 py-1 rounded-full border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">
          ⬇ AI narration (EN)
        </a>
        <a href="/campaign/pitch-voiceover-fr.mp3" download data-testid="pitch-voiceover-fr-link"
          className="text-[10px] font-bold px-2.5 py-1 rounded-full border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">
          ⬇ FR
        </a>
        <a href="/campaign/pitch-voiceover-sw.mp3" download data-testid="pitch-voiceover-sw-link"
          className="text-[10px] font-bold px-2.5 py-1 rounded-full border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">
          ⬇ SW
        </a>
        <audio controls src="/campaign/pitch-voiceover.mp3" className="h-7" data-testid="pitch-voiceover-player" />
      </div>
    </div>
  );
};
